import {axiosBaseQuery} from "@/helpers/axiosBaseQuery";
import api from "@/store/api/api";
import {createApi} from "@reduxjs/toolkit/query/react";

export const salaryApi = createApi({
    reducerPath: 'salaryApi',
    tagTypes: ['salary', 'salary-single'],
    baseQuery: axiosBaseQuery({
        baseUrl: api.root,
    }),
    endpoints: build => ({
        getSalary: build.query({
            query: (data) => ({
                url: api.salary.getSalary(),
                method: 'GET',
                params: data
            }),
            serializeQueryArgs: ({endpointName, queryArgs}) => {
                return [endpointName, ...(queryArgs?.filter_ids ?? [])];
            },
            merge: (currentCache, newItems, {arg}) => {
                if (arg.skip === 0) {
                    return newItems;
                }
                currentCache.push(
                    ...newItems.filter((newItem) => !currentCache.some((item) => item.id === newItem.id))
                );
            },
            forceRefetch: ({currentArg, previousArg}) => {
                return currentArg !== previousArg;
            },
            providesTags: ['salary']
        }),
        getSalaryReports: build.query({
            query: (data) => ({
                url: api.salary.getSalaryReports(),
                method: 'GET',
                params: data
            }),
            serializeQueryArgs: ({endpointName, queryArgs}) => {
                return [endpointName, ...(queryArgs?.filter_ids ?? [])];
            },
            merge: (currentCache, newItems, {arg}) => {
                if (arg.skip === 0) {
                    return newItems;
                }
                currentCache.push(
                    ...newItems.filter((newItem) => !currentCache.some((item) => item.id === newItem.id))
                );
            },
            forceRefetch: ({currentArg, previousArg}) => {
                return currentArg !== previousArg;
            },
            providesTags: ['salary']
        }),
        markSalaryReportPaid: build.mutation({
            query: (data) => ({
                url: api.salary.markSalaryReportPaid(),
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['salary']
        }),
        payDailySalaryReport: build.mutation({
            query: (data) => ({
                url: api.salary.payDailySalaryReport(),
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['salary']
        }),
        exportSalaryReports: build.mutation({
            query: (data) => ({
                url: api.salary.exportSalaryReports(),
                method: 'POST',
                body: data
            }),
        }),
        getSalaryTemplateById: build.query({
            query: ({id}) => ({
                url: api.salary.getSalaryTemplateById(id),
                method: 'GET',
            }),
            providesTags: (result, error, arg) =>
                result
                    ? [{type: 'salary-single', id: result.id}]
                    : ['salary-single'],
        }),
        getSalaryTemplateStaff: build.query({
            query: (data) => ({
                url: api.salary.getSalaryTemplateStaff(),
                method: 'GET',
                params: data
            }),
        }),
        getServicesGroupedByCategories: build.query({
            query: (data) => ({
                url: api.services.getServicesGroupedByCategories(),
                method: 'GET',
                params: data
            }),
            providesTags: ['staff-services'],
        }),
        getDailySalary: build.query({
            query: (data) => ({
                url: api.salary.getDailySalary(),
                method: 'GET',
                params: data
            }),
        }),
        createSalary: build.mutation({
            query: (data) => ({
                url: api.salary.createSalary(),
                method: 'POST',
                body: data
            }),
            async onQueryStarted(newSalary, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled;
                    dispatch(
                        salaryApi.util.updateQueryData('getSalary', {}, (draft) => {
                            draft.unshift(data);
                        })
                    );
                } catch (error) {
                    console.error('Failed to update cache after creating category:', error);
                }
            }
        }),
        editSalary: build.mutation({
            query: ({id, ...data}) => ({
                url: api.salary.editSalary(id),
                method: 'PUT',
                body: data
            }),
            async onQueryStarted(updatedSalary, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled;
                    dispatch(
                        salaryApi.util.updateQueryData('getSalary', {}, (draft) => {
                            const index = draft.findIndex(item => item.id === updatedSalary.id);
                            if (index !== -1) {
                                draft[index] = data;
                            }
                        })
                    );
                } catch (error) {
                    console.error('Failed to update cache after editing salary:', error);
                }
            },
            invalidatesTags: (result, error, arg) => [{type: 'salary-single', id: arg.id}],
        }),
        deleteSalary: build.mutation({
            query: ({id}) => ({
                url: api.salary.deleteSalary(id),
                method: 'DELETE',
            }),
            async onQueryStarted(deletedSalary, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(
                        salaryApi.util.updateQueryData('getSalary', {}, (draft) => {
                            const index = draft.findIndex(item => item.id === deletedSalary.id);
                            if (index !== -1) {
                                draft.splice(index, 1);
                            }
                        })
                    );
                } catch (error) {
                    console.error('Failed to update cache after deleting salary:', error);
                }
            }
        }),
        invalidatesTags: (result, error, arg) => [{type: 'salary-single', id: arg.id}],
    })
});

export const {
    useLazyGetSalaryQuery,
    useDeleteSalaryMutation,
    useGetSalaryTemplateByIdQuery,
    useGetSalaryTemplateStaffQuery,
    useGetServicesGroupedByCategoriesQuery,
    useGetDailySalaryQuery,
} = salaryApi
